// Treasury withdraw-requests module parser — DTO (wire shape) → view
// model. The seam every consumer must respect. Containers / components /
// composables never read a snake_case field directly.

import type {
  WithdrawRequestDetailDto,
  WithdrawRequestInvestorBankAccountDto,
  WithdrawRequestInvestorDto,
  WithdrawRequestRowDto,
  WithdrawRequestSettlementDto,
  WithdrawRequestStatusCountsDto,
  WithdrawRequestTreasuryAccountDto,
  WithdrawRequestsListResponseDto,
} from './withdrawRequestsContracts'
import type {
  WithdrawRequestDetail,
  WithdrawRequestInvestor,
  WithdrawRequestInvestorBankAccount,
  WithdrawRequestRow,
  WithdrawRequestSettlement,
  WithdrawRequestStatusCounts,
  WithdrawRequestTreasuryAccount,
  WithdrawRequestsListResponse,
} from '../types/withdrawRequestsTypes'

export function parseWithdrawRequestRow(dto: WithdrawRequestRowDto): WithdrawRequestRow {
  return {
    id: String(dto.id ?? ''),
    investorId: String(dto.investorId ?? dto.investor_id ?? ''),
    fiatWalletId: String(dto.fiatWalletId ?? dto.fiat_wallet_id ?? ''),
    bankAccountId: String(dto.bankAccountId ?? dto.bank_account_id ?? ''),
    status: dto.status,
    amount: String(dto.amount ?? '0'),
    currencyCode: String(dto.currencyCode ?? dto.currency_code ?? ''),
    externalReferenceId: dto.externalReferenceId ?? dto.external_reference_id ?? null,
    rejectionReason: dto.rejectionReason ?? dto.rejection_reason ?? null,
    approvedAt: dto.approvedAt ?? dto.approved_at ?? null,
    completedAt: dto.completedAt ?? dto.completed_at ?? null,
    createdAt: String(dto.createdAt ?? dto.created_at ?? ''),
    investorName: String(dto.investorName ?? dto.investor_name ?? ''),
    investorEmail: String(dto.investorEmail ?? dto.investor_email ?? ''),
  }
}

export function parseWithdrawRequestsListResponse(
  dto: WithdrawRequestsListResponseDto,
): WithdrawRequestsListResponse {
  const limit = Number(dto.meta?.limit ?? 0)
  const total = Number(dto.meta?.total ?? 0)
  const page = Number(dto.meta?.page ?? 1)
  const totalPagesRaw = dto.meta?.totalPages ?? dto.meta?.total_pages
  const totalPages =
    totalPagesRaw !== undefined && totalPagesRaw !== null
      ? Number(totalPagesRaw)
      : limit > 0
        ? Math.ceil(total / limit)
        : 0
  return {
    data: (dto.data ?? []).map(parseWithdrawRequestRow),
    meta: { total, page, limit, totalPages },
  }
}

export function parseWithdrawRequestStatusCounts(
  dto: WithdrawRequestStatusCountsDto,
): WithdrawRequestStatusCounts {
  return {
    pendingApproval: Number(dto.pendingApproval ?? dto.pending_approval ?? 0),
    awaitingSettlement: Number(dto.awaitingSettlement ?? dto.awaiting_settlement ?? 0),
    completed: Number(dto.completed ?? 0),
    rejected: Number(dto.rejected ?? 0),
  }
}

function parseInvestor(dto: WithdrawRequestInvestorDto): WithdrawRequestInvestor {
  return {
    id: String(dto.id ?? ''),
    firstName: dto.firstName ?? dto.first_name ?? null,
    lastName: dto.lastName ?? dto.last_name ?? null,
    email: String(dto.email ?? ''),
    kycStatus: dto.kycStatus ?? dto.kyc_status ?? null,
  }
}

function parseInvestorBankAccount(
  dto: WithdrawRequestInvestorBankAccountDto,
): WithdrawRequestInvestorBankAccount {
  return {
    id: String(dto.id ?? ''),
    bankName: String(dto.bankName ?? dto.bank_name ?? ''),
    accountHolderName: String(dto.accountHolderName ?? dto.account_holder_name ?? ''),
    iban: dto.iban ?? null,
    accountNumberMasked: String(dto.accountNumberMasked ?? dto.account_number_masked ?? ''),
  }
}

function parseTreasuryAccount(
  dto: WithdrawRequestTreasuryAccountDto,
): WithdrawRequestTreasuryAccount {
  return {
    id: String(dto.id ?? ''),
    accountName: String(dto.accountName ?? dto.account_name ?? ''),
    bankName: String(dto.bankName ?? dto.bank_name ?? ''),
    iban: dto.iban ?? null,
    currencyCode: String(dto.currencyCode ?? dto.currency_code ?? ''),
  }
}

function parseSettlement(dto: WithdrawRequestSettlementDto | undefined): WithdrawRequestSettlement {
  return {
    settlementReference: dto?.settlementReference ?? dto?.settlement_reference ?? null,
    settledAt: dto?.settledAt ?? dto?.settled_at ?? null,
  }
}

export function parseWithdrawRequestDetail(dto: WithdrawRequestDetailDto): WithdrawRequestDetail {
  const bankAccount = dto.investorBankAccount ?? dto.investor_bank_account
  const treasury = dto.treasuryBankAccount ?? dto.treasury_bank_account
  return {
    id: String(dto.id ?? ''),
    status: dto.status,
    amount: String(dto.amount ?? '0'),
    currencyCode: String(dto.currencyCode ?? dto.currency_code ?? ''),
    externalReferenceId: dto.externalReferenceId ?? dto.external_reference_id ?? null,
    rejectionReason: dto.rejectionReason ?? dto.rejection_reason ?? null,
    createdAt: String(dto.createdAt ?? dto.created_at ?? ''),
    investor: parseInvestor(dto.investor),
    investorBankAccount: bankAccount
      ? parseInvestorBankAccount(bankAccount)
      : {
          id: '',
          bankName: '',
          accountHolderName: '',
          iban: null,
          accountNumberMasked: '',
        },
    treasuryBankAccount: treasury ? parseTreasuryAccount(treasury) : null,
    settlement: parseSettlement(dto.settlement),
    audit: {
      approvedBy: dto.audit?.approvedBy ?? dto.audit?.approved_by ?? null,
      approvedAt: dto.audit?.approvedAt ?? dto.audit?.approved_at ?? null,
    },
  }
}
